import { Show } from "solid-js";
import { openModalV2 } from "~/shared_states/modal";
import { HowToUseModal } from "~/components/webstore_telescope/how_to_use_modal";
import { TncsModal } from "~/components/webstore_telescope/tncs_modal";

export type ActionsListProps = {
  brandId: string;
  productId?: string;
  onContactSupport: () => void;
  showContactSupport?: boolean;
};

/**
 * Action list items: How to redeem, Terms & conditions, Contact support
 */
export function ActionsList(props: ActionsListProps) {
  const openHowToUse = () => {
    openModalV2(() => <HowToUseModal brandId={props.brandId} />, {
      expandToFullHeight: true,
    });
  };

  const openTnc = () => {
    openModalV2(() => <TncsModal productId={props.productId!} />, {
      expandToFullHeight: true,
    });
  };

  return (
    <div class="flex flex-col overflow-hidden rounded-[12px] border border-stroke-1 stroke-text-normal-primary">
      {/* How to redeem */}
      <ActionItem
        icon={<QuestionIcon />}
        label="How to redeem"
        onClick={openHowToUse}
      />

      <Divider />

      {/* Terms & conditions */}
      <ActionItem
        icon={<DocumentIcon />}
        label="Terms & conditions"
        onClick={openTnc}
      />

      <Show when={props.showContactSupport !== false}>
        <Divider />

        {/* Contact support */}
        <ActionItem
          icon={<SupportIcon />}
          label="Contact support"
          onClick={props.onContactSupport}
        />
      </Show>
    </div>
  );
}

type ActionItemProps = {
  icon: any;
  label: string;
  onClick: () => void;
};

function ActionItem(props: ActionItemProps) {
  return (
    <button
      onClick={props.onClick}
      class="flex h-11 items-center gap-2 bg-background-normal-primary px-3 py-2 hover:bg-background-normal-secondary"
    >
      <div class="h-6 w-6">{props.icon}</div>
      <span class="flex-1 text-left text-para-3-medium text-text-normal-primary">
        {props.label}
      </span>
      <ChevronRightIcon />
    </button>
  );
}

function Divider() {
  return <div class="h-px w-full bg-stroke-1" />;
}

// Icons
function QuestionIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M12 21V21C7.029 21 3 16.971 3 12V12C3 7.029 7.029 3 12 3V3C16.971 3 21 7.029 21 12V12C21 16.971 16.971 21 12 21Z"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M12 13.25V13C12 12.183 12.505 11.74 13.011 11.4C13.505 11.067 14 10.633 14 9.83301C14 8.72801 13.105 7.83301 12 7.83301C10.895 7.83301 10 8.72801 10 9.83301"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M11.999 16C11.861 16 11.749 16.112 11.75 16.25C11.75 16.388 11.862 16.5 12 16.5C12.138 16.5 12.25 16.388 12.25 16.25C12.25 16.112 12.138 16 11.999 16"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M8 13H16"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8 17H16"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M6 3H15.172C15.702 3 16.211 3.211 16.586 3.586L19.414 6.414C19.789 6.789 20 7.298 20 7.828V19C20 20.105 19.105 21 18 21H6C4.895 21 4 20.105 4 19V5C4 3.895 4.895 3 6 3Z"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M20 8H16C15.448 8 15 7.552 15 7V3"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8 9H11"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M7.083 21.813C6.345 21.813 5.646 21.65 5.018 21.359L2 22L2.631 18.976C2.338 18.346 2.174 17.644 2.174 16.904C2.174 14.196 4.369 12 7.078 12C9.787 12 11.982 14.196 11.982 16.904C11.982 19.612 9.786 21.808 7.078 21.808"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.612 12.019C6.219 11.164 6 10.227 6 9.24C6 5.223 9.598 2 14 2C18.402 2 22 5.223 22 9.24C22 11.614 20.738 13.704 18.801 15.022C18.802 15.778 18.8 16.797 18.8 17.867L15.661 16.321C15.124 16.424 14.569 16.48 14 16.48C13.289 16.48 12.602 16.388 11.944 16.23"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M4.79512 16.9646C4.81465 16.9842 4.81465 17.0158 4.79512 17.0354C4.77559 17.0549 4.74394 17.0549 4.72441 17.0354C4.70488 17.0158 4.70488 16.9842 4.72441 16.9646C4.74394 16.9451 4.77559 16.9451 4.79512 16.9646"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M7.04512 16.9646C7.06465 16.9842 7.06465 17.0158 7.04512 17.0354C7.02559 17.0549 6.99394 17.0549 6.97441 17.0354C6.95488 17.0158 6.95488 16.9842 6.97441 16.9646C6.99394 16.9451 7.02559 16.9451 7.04512 16.9646"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M9.29512 16.9646C9.31465 16.9842 9.31465 17.0158 9.29512 17.0354C9.27559 17.0549 9.24394 17.0549 9.22441 17.0354C9.20488 17.0158 9.20488 16.9842 9.22441 16.9646C9.24394 16.9451 9.27559 16.9451 9.29512 16.9646"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14.0001 10.0442C14.0001 9.38718 14.4061 9.03118 14.8131 8.75718C15.2111 8.48918 15.6091 8.14018 15.6091 7.49718C15.6091 6.60818 14.8891 5.88818 14.0001 5.88818C13.1111 5.88818 12.3911 6.60818 12.3911 7.49718"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14 12.4581C13.945 12.4581 13.9 12.5031 13.9 12.5581C13.9 12.6131 13.945 12.6581 14 12.6581C14.055 12.6581 14.1 12.6131 14.1 12.5581C14.1 12.5031 14.055 12.4581 14 12.4581"
        class="stroke-inherit"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.5 15l5-5-5-5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-text-normal-tertiary"
      />
    </svg>
  );
}
